import React, { Fragment, useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useSnackbar } from 'notistack';
import { TextField } from 'mui-rff';
import { Box, Button, Collapse, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
//
import Title from 'components/common/Title';
import { useBoolFlag } from 'hooks/index';
import { createSchemaValidator } from 'utils/validate';
import { USER_PROFILE_VALIDATION_SCHEMA } from 'utils/validation.schema';
import { formatRoleToLabel, handleApiError } from 'utils/common';
import {
  useGetCurrentUserQuery,
  useUpdateCurrentUserMutation,
} from '../../../store/api/user.api';

const InfoBox = styled(Box)(() => ({
  display: 'inline-grid',
  gap: '8px 16px',
  gridTemplateColumns: 'auto 1fr',
  ['& .MuiTypography-root']: {
    ['&:nth-of-type(odd)']: {
      ['&::after']: {
        content: '":"',
      },
    },
  },
}));

const fields = [
  ['id', 'ID'],
  ['name', 'Name'],
  ['email', 'Email'],
  ['role', 'Role', formatRoleToLabel],
];
export default function UserProfile() {
  const { data: user } = useGetCurrentUserQuery();
  const [mutateAsync] = useUpdateCurrentUserMutation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    isOn: formVisible,
    setOn: showForm,
    setOff: hideForm,
  } = useBoolFlag();
  // using this construction to wipe out initial data on each collapse toggle
  const initialValues = useMemo(() => ({}), [formVisible]);
  const validationFn = useMemo(
    () => createSchemaValidator(USER_PROFILE_VALIDATION_SCHEMA),
    [],
  );

  const updateProfile = useCallback(
    async (values) => {
      try {
        await mutateAsync(values).unwrap();
        hideForm();
        enqueueSnackbar('Profile successfully updated', { variant: 'success' });
      } catch (err) {
        handleApiError(err, 'Profile update failed');
      }
    },
    [mutateAsync, enqueueSnackbar, hideForm],
  );

  return (
    <Paper
      sx={{
        mx: '32px',
        p: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Title>User Profile</Title>
      <InfoBox>
        {fields.map(([fieldKey, fieldLabel, transformer]) => (
          <Fragment key={fieldKey}>
            <Typography fontWeight='bold'>{fieldLabel}</Typography>
            <Typography>
              {transformer?.(user[fieldKey]) || user[fieldKey]}
            </Typography>
          </Fragment>
        ))}
      </InfoBox>
      <Box maxWidth='480px'>
        <Collapse in={formVisible} mountOnEnter unmountOnExit>
          <Form
            initialValues={initialValues}
            validate={validationFn}
            onSubmit={updateProfile}
            render={({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit} autoComplete='off'>
                <Box>
                  <TextField
                    name='oldPassword'
                    autoComplete='off'
                    label='Current Password'
                    type='password'
                  />
                  <TextField
                    name='password'
                    autoComplete='off'
                    label='New Password'
                    type='password'
                  />
                  <TextField
                    name='confirmPassword'
                    autoComplete='off'
                    label='Confirm New Password'
                    type='password'
                  />
                </Box>
                <Box mt='16px' display='flex' gap='16px'>
                  <Button
                    variant='outlined'
                    disabled={submitting}
                    onClick={hideForm}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='contained'
                    disabled={submitting}
                    type='submit'
                    value='submit'
                  >
                    Submit
                  </Button>
                </Box>
              </form>
            )}
          />
        </Collapse>
        {!formVisible ? (
          <Button variant='contained' onClick={showForm}>
            Change password
          </Button>
        ) : null}
      </Box>
    </Paper>
  );
}
